/**
 * Delete segments (W3-05; UX-037, RPE EVT-07 "delete event"). A segment is the curve between two keys; deleting it
 * removes its motion the way RPE removes an event, so the line keeps the value it had at the segment's start:
 *
 *  - a segment in the middle of a track becomes a Stay ("Stay, then jump", DESIGN.md §5.6.2): the value holds from
 *    its start key, and the next key's own leave value continues as before (a visible jump, as an RPE gap between
 *    two events);
 *  - the LAST segment of a track loses its end key: the line holds the start value afterwards (RPE: nothing after the
 *    last event's end, the value is held);
 *  - a segment that already is a Stay (or an imported gap) in the middle of a track is left as it is (counted in
 *    `unchanged`).
 *
 * Built from `keyCommands.toggleHold` / `deleteKeys`, so X/Y-linked lines and link groups follow as they do there.
 */

import type { KeyId } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { keyCommands } from './keys';
import { MOTION_MSG, reason } from './motionTrack';

export interface DeleteSegmentsResult {
  /** Middle segments that became Stays. */
  readonly stayed: number;
  /** Last segments whose end key was deleted. */
  readonly trimmed: number;
  /** Segments that already were Stays in the middle of their track. */
  readonly unchanged: number;
}

/** What `deleteSegments` will do with each segment start key (read-only; for previews, enable checks, tests). */
export function planSegmentDelete(
  view: Pick<TxBuilder, 'doc' | 'idx'>,
  segments: readonly KeyId[],
): { readonly stay: KeyId[]; readonly trimEnds: KeyId[]; readonly unchanged: KeyId[] } {
  const stay: KeyId[] = [];
  const trimEnds: KeyId[] = [];
  const unchanged: KeyId[] = [];
  for (const id of new Set(segments)) {
    const k = view.doc.keys.get(id);
    const end = k === undefined ? undefined : view.idx.nextKey(id);
    if (k === undefined || end === undefined) continue;
    if (view.idx.nextKey(end) === undefined) trimEnds.push(end);
    else if (k.ease.kind === 'hold') unchanged.push(id);
    else stay.push(id);
  }
  return { stay, trimEnds, unchanged };
}

/** Deletes segments (identified by their start keys) as described in the file header. */
export function deleteSegments(tx: TxBuilder, a: { readonly segments: readonly KeyId[] }): DeleteSegmentsResult {
  const plan = planSegmentDelete(tx, a.segments);
  if (plan.stay.length + plan.trimEnds.length + plan.unchanged.length === 0) tx.fail(reason(MOTION_MSG.noSegments));
  // Stays first: deleting an end key could otherwise make a staying segment the last one.
  if (plan.stay.length > 0) keyCommands.toggleHold(tx, { segments: plan.stay });
  const ends = plan.trimEnds.filter((id) => tx.doc.keys.has(id));
  if (ends.length > 0) keyCommands.deleteKeys(tx, { keys: ends });
  return { stayed: plan.stay.length, trimmed: ends.length, unchanged: plan.unchanged.length };
}
